import { useEffect, useRef, useState, useSyncExternalStore } from 'react'
import { Building2, FolderOpen, ScanLine } from 'lucide-react'
import { useAppStore } from '@core/store'
import { viewerApi } from '@viewer/lib/viewerApi'
import { useSampleLoad } from '@viewer/lib/useSampleLoad'
import {
  canOpenPointClouds,
  modelAcceptAttr,
} from '@core/session/modelFileTypes'
import { POINT_CLOUD_ACCEPT, pointCloudApi } from '@viewer/point-cloud/pointCloudApi'
import { ifcxApi } from '@viewer/ifcx/ifcxApi'

// Centered call-to-action shown over the empty viewport when no model is
// loaded. Gives first-time visitors (and phones, where there's often no
// file to hand) a one-tap way in: open a sample, or pick an IFC. Hides as
// soon as a model loads or a load is in progress.
export default function EmptyState() {
  const modelsCount = useAppStore((s) => s.models.length)
  // Uma nuvem de pontos ou um IFC5 também é "ter alguma coisa aberta".
  // Contava só IFC, e quem abrisse um levantamento ficava com esta caixa
  // por cima dele, obrigado a abrir um IFC para a fazer desaparecer.
  const nuvens = useSyncExternalStore(pointCloudApi.subscribe, pointCloudApi.list, () => [])
  const ifc5 = useSyncExternalStore(ifcxApi.subscribe, ifcxApi.lista, () => [])
  // Durante os três minutos de um PTS grande a nuvem ainda não está na
  // lista, e esta caixa ficava a pedir que se abrisse alguma coisa por
  // cima da barra de progresso de uma coisa que já se estava a abrir.
  const nuvemAEntrar = useSyncExternalStore(
    pointCloudApi.subscribe,
    pointCloudApi.cargaEmCurso,
    () => null,
  )
  const fileRef = useRef<HTMLInputElement | null>(null)
  const nuvemRef = useRef<HTMLInputElement | null>(null)
  const setNuvemAberta = useAppStore((s) => s.setPointCloudPanelOpen)
  const { busyId, run: runSample } = useSampleLoad()
  const busy = busyId !== null
  const [loading, setLoading] = useState(false)
  const [loaderReady, setLoaderReady] = useState(viewerApi.hasIfcLoader())

  useEffect(
    () => viewerApi.subscribe(() => setLoaderReady(viewerApi.hasIfcLoader())),
    [],
  )
  useEffect(() => {
    const onStage = (e: Event) => {
      const d = (e as CustomEvent).detail as { loading: boolean } | undefined
      if (d) setLoading(d.loading)
    }
    window.addEventListener('vn2r:viewer-stage', onStage as EventListener)
    return () =>
      window.removeEventListener('vn2r:viewer-stage', onStage as EventListener)
  }, [])

  if (
    modelsCount > 0 ||
    nuvens.length > 0 ||
    ifc5.length > 0 ||
    nuvemAEntrar !== null ||
    loading ||
    busy
  ) {
    return null
  }

  return (
    <div className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center p-4">
      <div className="pointer-events-auto w-full max-w-sm rounded-xl border border-[var(--color-border)] bg-[var(--color-panel)]/90 p-5 text-center shadow-2xl backdrop-blur">
        <div className="mb-1 text-sm font-medium text-[var(--color-text)]">
          Sem modelo carregado
        </div>
        <p className="mb-4 text-xs leading-relaxed text-[var(--color-text-muted)]">
          Abre um ficheiro IFC ou uma nuvem de pontos do teu dispositivo (ou
          arrasta-o para aqui), ou experimenta um modelo de exemplo.
        </p>
        <div className="flex flex-col gap-2">
          <button
            type="button"
            onClick={() => void runSample()}
            disabled={!loaderReady}
            className="flex items-center justify-center gap-2 rounded-md border border-[var(--color-accent-border)] bg-[var(--color-accent-bg)] px-3 py-2 text-sm font-medium text-[var(--color-accent)] hover:text-[var(--color-accent-hover)] disabled:opacity-50"
          >
            <Building2 size={16} /> Abrir exemplo
          </button>
          <button
            type="button"
            onClick={() => fileRef.current?.click()}
            disabled={!loaderReady}
            className="flex items-center justify-center gap-2 rounded-md border border-[var(--color-border)] px-3 py-2 text-sm text-[var(--color-text-muted)] hover:text-[var(--color-text)] disabled:opacity-50"
          >
            <FolderOpen size={16} /> Abrir modelo
          </button>
          {canOpenPointClouds() && (
            // Uma nuvem abre-se sozinha, sem IFC nenhum. Esta caixa só
            // oferecia IFC, e quem viesse com um levantamento tinha de
            // descobrir o painel das nuvens, ou abrir um IFC que não
            // queria para a caixa sair da frente.
            <button
              type="button"
              onClick={() => nuvemRef.current?.click()}
              className="flex items-center justify-center gap-2 rounded-md border border-[var(--color-border)] px-3 py-2 text-sm text-[var(--color-text-muted)] hover:text-[var(--color-text)]"
            >
              <ScanLine size={16} /> Abrir nuvem de pontos
            </button>
          )}
        </div>
        {!loaderReady && (
          <div className="mt-3 text-[10px] text-[var(--color-text-dim)]">
            A preparar o viewer…
          </div>
        )}
        <input
          ref={fileRef}
          type="file"
          accept={modelAcceptAttr()}
          className="hidden"
          onChange={async (e) => {
            const f = e.target.files?.[0]
            if (f) await viewerApi.loadIfcFile(f, { replace: true })
            e.target.value = ''
          }}
        />
        <input
          ref={nuvemRef}
          type="file"
          accept={POINT_CLOUD_ACCEPT}
          className="hidden"
          onChange={(e) => {
            const f = e.target.files?.[0]
            e.target.value = ''
            if (!f) return
            setNuvemAberta(true)
            void pointCloudApi.abrir(f)
          }}
        />
      </div>
    </div>
  )
}
